import { Lottery } from "@sazka/lotteries/js/models/lottery";
import { LotteryParser } from "@sazka/lotteries/js/utils/lottery-parser";
import { ResponsiveUtils } from "@sazka/utils/responsive-utils";
import { AbTestAnalytics } from "./analytics-ab-test";
import { analyticsCollectorCasinoTopBanner } from "./analytics-banners";
import { useAnalyticsCollectActions } from "./analytics-collect-actions";
import { analyticsCollectorHeroCards } from "./analytics-hero-card";
import { AnalyticsPromo } from "./analytics-promo";
import { analyticsCollectorTicketClick } from "./analytics-ticket-click";
import { AnalyticsVertical } from "./analytics-vertical";

window.dataLayer = window.dataLayer || [];
const promos: AnalyticsPromo[] = [];
let isBundlesLoadedLock = false;
let isDomReadyLock = false;
// TODO - maybe add dinamic vue components as well for example lobby would use it

export namespace Analytics {
    export const vertical = getVertical();

    export const domReady = () => {
        isDomReadyLock = true;
        if (isBundlesLoadedLock) collect();
    };

    export const bundlesLoaded = () => {
        isBundlesLoadedLock = true;
        if (isDomReadyLock) collect();
    };

    export const addPromo = (id: string, name: string, relativeImgUrlOrText: string, position: string) => {
        promos.push({
            id: id,
            name: name,
            creative: getCreative(relativeImgUrlOrText),
            position: position,
        });
    };

    export function addPromoHeroCard(elem: Element, name: string, position: string, imgSelector?: string) {
        const $elem = $(elem);
        const $image = $elem.find(imgSelector || "img").first();
        Analytics.addPromo($elem.data("a-codename"), name, $image.attr("src") || $image.attr("data-src"), position);
    }

    export const actionShowPromo = (id: string, name: string, relativeImgUrlOrText: string, position: string) => {
        dataLayer.push({
            event: "action.show.promos",
            action: {
                show: {
                    promos: [
                        {
                            id: id,
                            name: name,
                            creative: getCreative(relativeImgUrlOrText),
                            position: position,
                        },
                    ],
                },
            },
        });
    };

    export const actionImpressPromo = (id: string, name: string, relativeImgUrlOrText: string, position: string) => {
        dataLayer.push({
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: [
                        {
                            id: id,
                            name: name,
                            creative: getCreative(relativeImgUrlOrText),
                            position: position,
                        },
                    ],
                },
            },
        });
    };

    export function actionShowPromoHeroCard(elem: Element, name: string, position: string, imgSelector?: string) {
        const $elem = $(elem);
        const $image = $elem.find(imgSelector || "img").first();
        Analytics.actionShowPromo(
            $elem.data("a-codename"),
            name,
            $image.attr("src") || $image.attr("data-src"),
            position
        );
    }

    export function actionClosePromoHeroCard(elem: Element, name: string, position: string, imgSelector?: string) {
        const $elem = $(elem);
        const $image = $elem.find(imgSelector || "img").first();
        Analytics.actionShowPromo(
            $elem.data("a-codename") + ".close",
            name,
            $image.attr("src") || $image.attr("data-src"),
            position
        );
    }

    export function actionImpressPromoHeroCard(elem: Element, name: string, position: string, imgSelector?: string) {
        const $elem = $(elem);
        const $image = $elem.find(imgSelector || "img").first();
        Analytics.actionImpressPromo(
            $elem.data("a-codename"),
            name,
            $image.attr("src") || $image.attr("data-src"),
            position
        );
    }

    export const getLotteryCode = (lottery: Lottery | string) => {
        const l = LotteryParser.parseLotteryName(lottery);
        switch (l) {
            case "stastnych-10":
                return "stastnych10";
            case "extra-renta":
                return "extrarenta";
            case "stastne-datum":
                if (document.getElementById("stastne-datum-orloj")) return "stastne-datum.orloj";
                else if (document.getElementById("stastne-datum-horoskop")) return "stastne-datum.horoskop";
                else return "stastnedatum";
            case "vsechno-nebo-nic":
                return "VNN";
            default:
                return lottery;
        }
    };

    export const lotteryCode = () => {
        if (!environment.game) return "no_game";
        return getLotteryCode(environment.game);
    };

    export const pageReady = () => {
        dataLayer.push({
            event: "pageReady",
        });
    };

    export const actionPushPromos = () => {
        pushPromos();
    };

    export const getBannerPosition = (element: HTMLElement, useMobileUnits = true) => {
        const elem = $(element.parentElement);
        const row = elem.closest(".bs-row");
        const column = elem.closest(row.children());
        if (column.is(":last-child")) {
            return useMobileUnits && ResponsiveUtils.isMobile() ? "bottom" : "right";
        } else {
            return useMobileUnits && ResponsiveUtils.isMobile() ? "top" : "left";
        }
    };

    export const actionTouchpointLotteryPath = (
        lotteryName: string,
        stringBeforeLotteryName: string,
        stringAfterLotteryName: string
    ) => {
        const lottery = LotteryParser.parseLotteryName(lotteryName);
        const lotteryCode = LotteryParser.analyticsCodeName(lottery);
        window.dataLayer.push({
            event: "action.touchpoint.lotteryPath",
            action: {
                touchpoint: {
                    path: `${stringBeforeLotteryName}${lotteryCode}${stringAfterLotteryName}`,
                },
            },
        });
    };

    export const getDrawDaysAndTimes = (drawNames: string[]) => {
        const times = drawNames?.filter((x) => ["evn", "aft", "afternoon", "evening"].includes(x.toLowerCase()));
        const days = drawNames?.filter((val) => {
            return !times.find((val2) => {
                return val === val2;
            });
        });

        const timesFormatted = times
            ?.map((d) => d.substring(0, 3))
            .join("")
            ?.toLowerCase();
        const daysFormatted = days
            ?.map((d) => d.substring(0, 2))
            ?.join("")
            ?.toLowerCase();

        return { days: daysFormatted, times: timesFormatted };
    };

    export const getAddonName = (lottery: Lottery, isAddonPlayed: boolean) => {
        if (!isAddonPlayed) {
            return null;
        }

        return LotteryParser.getAddon(lottery);
    };

    export const pushMenuClick = (codename: string, placement: "menu" | "footer") => {
        window.dataLayer.push({
            event: "action.click.button",
            action: {
                click: {
                    name: codename,
                    eventProperty2: placement,
                },
            },
        });
    };
}

function collect() {
    analyticsCollectorHeroCards();
    analyticsCollectorCasinoTopBanner();
    AbTestAnalytics.pushAbTest();

    // TODO newTicketBannerAnalytics - add to widget file, when widget is created
    pushPromos();

    if (!window.disablePushPageResponsiveDesignMode) pushResponsiveDesignMode();
    if (!window.disablePushPage && window.landingPageVertical) pushLandingPage();
    if (!window.disablePageReady) Analytics.pageReady();

    analyticsCollectorTicketClick();

    useAnalyticsCollectActions();
}

function pushResponsiveDesignMode() {
    dataLayer.push({ page: { responseDesignCategory: ResponsiveUtils.getDataLayerResolution() } });
}

function pushPromos() {
    if (promos.length) {
        dataLayer.push({
            impression: {
                promos: promos,
            },
        });
    }
}

function getVertical(): AnalyticsVertical {
    const path = environment.nodeAliasPath || window.location.pathname;
    if (!path) {
        return "";
    } else if (path.startsWith("/losy/stiraci-losy")) {
        return "losy";
    } else if (path.startsWith("/losy")) {
        return "elosy";
    } else if (path.startsWith("/hry")) {
        return "hry";
    } else if (path.startsWith("/loterie")) {
        return "loterie";
    }
    return "";
}

function getCreative(relativeImageUrl: string): string {
    return (relativeImageUrl || "").startsWith("/") ? window.location.hostname + relativeImageUrl : relativeImageUrl;
}

function pushLandingPage() {
    dataLayer.push({
        page: {
            type: "LP",
            kind: "LP",
            gameCode: "no_game",
            gameCodeStrict: "no_game",
            verticalCode: landingPageVertical,
            verticalCodeStrict: landingPageVertical,
        },
    });
}
